<template>
    <b-tabs v-model="tab">
        <b-tab title="Tryckfil">
            <p v-if="error" class="alert alert-danger mt-3" role="alert">{{ error }}</p>
            <p v-if="notice" class="alert alert-success mt-3" role="status">{{ notice }}</p>
            <div v-if="approved" class="border rounded p-3 my-3">
                <strong>Underlag: <a :href="approved.url" target="_blank" rel="noopener">Godkänt korr</a></strong>
                <p class="mb-0">Tryckformat: {{ approved.print_format }} · {{ approved.print_geometry.w }} × {{ approved.print_geometry.h }} mm. Format och hälsning är låsta till korret.</p>
            </div>
            <p v-else class="alert alert-warning mt-3">Godkänn ett aktuellt korr i orderns korrflöde innan du skapar tryckfil.</p>
            <div v-if="approved" class="my-3">
                <button type="button" class="btn btn-light" :disabled="busy || state.unsaved_source_selection" @click="exportIndesign">Ladda ner för InDesign</button>
                <small class="d-block text-muted">IDML med separata text- och bildobjekt när korret kommer från designverktyget. En vald bildhälsning förblir en bild. Kontrollera exporten mot medföljande PDF.</small>
                <span v-if="exporting" class="d-block text-muted" role="status">Skapar och laddar ner InDesign-paket…</span>
            </div>
            <p v-if="state.unsaved_source_selection" class="alert alert-warning">Spara orderns bildval innan du skapar tryckfil.</p>
            <div v-if="!editing && displayFile" class="mt-3">
                <p v-if="displayFile.source_proof_id !== state.approved" class="alert alert-warning">Den här tryckfilen tillhör ett tidigare korr. Generera om den från aktuellt godkänt korr innan den används för ny produktion.</p>
                <p class="text-muted">{{ settings(displayFile) }}</p>
                <div class="d-flex flex-wrap mb-3" style="gap: 8px;">
                    <a :href="displayFile.url" class="btn btn-light" target="_blank" rel="noopener">Öppna tryckfil</a>
                    <a :href="displayFile.url + '?download=1'" class="btn btn-primary">Ladda ner tryckfil</a>
                    <button type="button" class="btn btn-light" :disabled="busy || !approved || state.unsaved_source_selection" @click="showForm">Generera om tryckfil</button>
                    <button type="button" class="btn btn-light" :disabled="busy" @click="$emit('close')">Stäng</button>
                </div>
                <iframe :key="displayFile.id" :src="displayFile.url" title="Sparad tryckfil" style="display: block; width: 100%; height: 65vh; border: 0;"></iframe>
            </div>
            <form v-else @submit.prevent="generate">
                <fieldset :disabled="busy || !approved || state.unsaved_source_selection">
                    <p class="text-muted mt-3">Lämna kortintervallet tomt för en sida utan kortnummer. Giltighetsdatum är valfritt och kan skrivas ut även utan kortnummer.</p>
                    <div class="row">
                        <div class="col-md-6 form-group"><label for="production-card-from">Startkort</label><input id="production-card-from" v-model="options.card_from" class="form-control" placeholder="CardUID" maxlength="64"></div>
                        <div class="col-md-6 form-group"><label for="production-card-to">Slutkort</label><input id="production-card-to" v-model="options.card_to" class="form-control" placeholder="CardUID" maxlength="64"></div>
                        <div class="col-md-6 form-group"><label for="production-valid-until">Giltigt till</label><input id="production-valid-until" v-model="options.valid_until" type="date" class="form-control"></div>
                        <div class="col-md-6 form-group">
                            <label for="production-lang">Språk</label>
                            <select id="production-lang" v-model="options.lang" class="form-control"><option value="sv">Svenska</option><option value="en">Engelska</option><option value="fi">Finska</option></select>
                        </div>
                    </div>
                    <p class="text-muted">PDF:en sparas som en ny version på ordern. Tidigare filer behålls.</p>
                    <button type="submit" class="btn btn-primary">Generera och spara tryckfil</button>
                </fieldset>
                <button type="button" class="btn btn-light mt-2" :disabled="busy" @click="cancelForm">Avbryt</button>
                <span v-if="busy && !exporting" class="ml-2" role="status">Skapar och sparar tryckfil…</span>
            </form>
        </b-tab>
        <b-tab title="Historik" :disabled="busy">
            <p class="text-muted mt-3">Sparade korr och tryckfiler, senaste först. Öppna en sparad design för att fortsätta redigera den som ett osparat utkast.</p>
            <p v-if="!versions.length" class="text-muted">Inga produktionsfiler har sparats.</p>
            <div v-else class="table-responsive">
                <table class="table table-sm">
                    <thead><tr><th>Version</th><th>Skapad</th><th>Underlag och format</th><th>Tryckinställningar</th><th>Status</th><th>Åtgärder</th></tr></thead>
                    <tbody>
                        <tr v-for="item in versions" :key="item.id">
                            <td>{{ item.kind === 'korr' ? 'Korr' : 'Tryckfil' }} · {{ item.sequence }}</td>
                            <td>{{ date(item.created_at) }}<small v-if="item.creator_name" class="d-block">{{ item.creator_name }}</small></td>
                            <td>{{ sourceLabel(item.source) }}<small class="d-block">{{ item.format }} · {{ item.geometry.w }} × {{ item.geometry.h }} mm</small></td>
                            <td>{{ item.kind === 'tryck' ? settings(item) : '—' }}</td>
                            <td>
                                <span v-if="state.current[item.kind] === item.id" class="badge badge-light d-block mb-1">Aktuell version</span>
                                <span v-if="item.approved" class="badge badge-success d-block mb-1">Godkänt korr</span>
                                <span v-else-if="item.approved_at" class="badge badge-light d-block mb-1">Tidigare godkänt</span>
                                <span v-if="item.kind === 'tryck' && item.source_proof_id !== state.approved" class="badge badge-warning d-block mb-1">Tidigare tryckunderlag</span>
                                <span v-if="item.final" class="badge badge-light d-block">Arkivskyddad</span>
                            </td>
                            <td>
                                <a :href="item.url" target="_blank" rel="noopener">Öppna</a>
                                <a :href="item.url + '?download=1'" class="d-block">Ladda ner</a>
                                <router-link v-if="item.kind === 'korr' && item.source === 'draft' && item.layout" :to="designRoute(item)">Öppna i designern</router-link>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <button type="button" class="btn btn-light" :disabled="busy" @click="$emit('close')">Stäng</button>
        </b-tab>
    </b-tabs>
</template>

<script>
import apiClient from '@services/ApiClient';
import {
    productionState, createApprovedPrint, downloadIndesign, productionError,
} from '@services/GreetingProductionApi';

const defaults = () => ({
    card_from: '', card_to: '', valid_until: '', lang: 'sv',
});

export default {
    props: {
        orderId: { type: [Number, String], required: true },
        state: { type: Object, required: true },
        initialTab: { type: String, default: 'print' },
    },
    data: () => ({
        tab: 0, busy: false, error: '', notice: '', options: defaults(), createdFile: null, editing: false, exporting: false,
    }),
    computed: {
        production() { return productionState(this.state); },
        approved() { return this.production.approved; },
        versions() { return Object.values(this.state.versions || {}).sort((a, b) => b.sequence - a.sequence); },
        displayFile() { return this.createdFile || this.currentPrint; },
        currentPrint() { return this.production.printFile; },
    },
    watch: {
        options: { deep: true, handler() { if (this.editing) this.notice = ''; } },
        initialTab(value) { this.tab = value === 'history' ? 1 : 0; },
    },
    created() {
        this.tab = this.initialTab === 'history' ? 1 : 0;
        this.editing = !this.displayFile;
        this.options = { ...defaults(), ...(this.displayFile?.print_options || {}) };
    },
    methods: {
        showForm() {
            if (this.busy || !this.approved || this.state.unsaved_source_selection) return;
            this.options = { ...defaults(), ...(this.displayFile?.print_options || {}) };
            this.error = ''; this.notice = ''; this.editing = true;
        },
        cancelForm() {
            if (this.busy) return;
            this.error = ''; this.notice = '';
            this.options = { ...defaults(), ...(this.displayFile?.print_options || {}) };
            if (this.displayFile) this.editing = false;
            else this.$emit('close');
        },
        date(value) { return new Date(value).toLocaleString('sv-SE'); },
        sourceLabel(value) {
            return {
                draft: 'Designutkast', manual_image: 'Vald hälsningsbild', order: 'Orderhälsning', approved: 'Godkänt korr',
            }[value] || 'Äldre underlag';
        },
        designRoute(item) { return { name: 'cardorders.greeting-design', params: { id: this.orderId }, query: { proof_version: item.id } }; },
        settings(item) {
            const settings = item.print_options;
            if (!settings) return 'Utan sparade tryckinställningar';
            const range = settings.card_from ? `${settings.card_from}–${settings.card_to}` : 'Utan kortnummer';
            const lang = { sv: 'Svenska', en: 'Engelska', fi: 'Finska' }[settings.lang] || settings.lang;
            return `${range} · ${settings.valid_until || 'Utan giltighetsdatum'} · ${lang}`;
        },
        async exportIndesign() {
            if (this.busy || !this.approved || this.state.unsaved_source_selection) return;
            this.error = ''; this.notice = ''; this.busy = true; this.exporting = true; this.$emit('busy', true);
            try {
                await downloadIndesign(apiClient, this.orderId, this.state);
                this.notice = 'InDesign-paketet har skapats och nedladdningen har startat.';
            } catch (error) { this.error = await productionError(error); } finally { this.busy = false; this.exporting = false; this.$emit('busy', false); }
        },
        async generate() {
            if (this.busy || !this.approved || this.state.unsaved_source_selection) return;
            this.error = ''; this.notice = '';
            if (!!this.options.card_from?.trim() !== !!this.options.card_to?.trim()) {
                this.error = 'Ange både start- och slutkort, eller lämna båda tomma.';
                return;
            }
            this.busy = true; this.$emit('busy', true);
            try {
                const result = await createApprovedPrint(apiClient, this.orderId, this.state, this.options);
                this.createdFile = result.file;
                this.editing = false;
                this.$emit('updated', result.state);
                this.notice = 'Tryckfilen har sparats som en ny version. Du kan nu öppna eller ladda ner den.';
            } catch (error) { this.error = await productionError(error); } finally { this.busy = false; this.$emit('busy', false); }
        },
    },
};
</script>
